import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import {
  createMarkerIcon,
  getPointType,
  getPointImage,
  getLinkedLocation,
  getPointAddress,
  getDirectionsUrl,
} from "./mapPointTypes";

export const QUEBEC_CENTER = [46.8, -72.2];
export const QUEBEC_ZOOM = 6;

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>';

// Fit the viewport to the markers whenever the set of markers changes.
const FitToPoints = ({ points, enabled }) => {
  const map = useMap();
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const signature = points.map((p) => p.id).join("|");

  useEffect(() => {
    if (!enabled) return;
    const pts = pointsRef.current;
    if (pts.length === 0) {
      map.setView(QUEBEC_CENTER, QUEBEC_ZOOM);
      return;
    }
    const bounds = L.latLngBounds(pts.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 13 });
  }, [signature, enabled, map]);

  return null;
};

// Fly to a point and open its popup when the parent asks for it (e.g. from the list).
const FocusHandler = ({ focus, points, markerRefs }) => {
  const map = useMap();
  const pointsRef = useRef(points);
  pointsRef.current = points;

  useEffect(() => {
    if (!focus) return;
    const point = pointsRef.current.find((p) => p.id === focus.id);
    if (!point) return;
    const openPopup = () => markerRefs.current[point.id]?.openPopup();
    map.flyTo([point.lat, point.lng], Math.max(map.getZoom(), 14), {
      duration: 0.8,
    });
    map.once("moveend", openPopup);
    return () => map.off("moveend", openPopup);
  }, [focus, map, markerRefs]);

  return null;
};

// Fly to an arbitrary target (used by the address search in the form).
const FlyToTarget = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lng], target.zoom || 16, { duration: 0.8 });
  }, [target, map]);
  return null;
};

const ClickHandler = ({ onMapClick }) => {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
};

// Keep Leaflet in sync with container size changes (modals, iframes, responsive layouts).
const ResizeHandler = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const timer = setTimeout(() => map.invalidateSize(), 150);
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(container);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [map]);
  return null;
};

const PointPopup = ({ point, locationsById, embed }) => {
  const type = getPointType(point.type);
  const Icon = type.Icon;
  const linked = getLinkedLocation(point, locationsById);
  const image = getPointImage(point, locationsById);
  const address = getPointAddress(point);
  const locationHref = linked ? `/locations/${linked.id}` : null;
  const linkClass = "text-amber-400 hover:text-amber-300";

  return (
    <div className="w-56 text-slate-50">
      {image && (
        <img
          src={image}
          alt=""
          loading="lazy"
          className="w-full h-28 object-cover rounded-md mb-2"
        />
      )}
      <div
        className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide mb-1"
        style={{ color: type.color }}
      >
        <Icon size={14} /> {type.label}
      </div>
      <h4 className="font-bold text-base leading-tight">{point.name}</h4>
      {address && <p className="text-sm text-slate-300 mt-1">{address}</p>}
      {linked?.table && (
        <p className="text-xs text-slate-400 mt-0.5">Table : {linked.table}</p>
      )}
      {point.description && (
        <p className="text-sm text-slate-300 mt-2">{point.description}</p>
      )}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-sm font-semibold">
        {locationHref &&
          (embed ? (
            <a
              href={`${window.location.origin}${locationHref}`}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              Inscription
            </a>
          ) : (
            <Link to={locationHref} className={linkClass}>
              Inscription
            </Link>
          ))}
        {point.website && (
          <a
            href={point.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-400 hover:text-sky-300"
          >
            Site web
          </a>
        )}
        <a
          href={getDirectionsUrl(point)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-300 hover:text-white"
        >
          Itinéraire
        </a>
      </div>
    </div>
  );
};

/**
 * Leaflet map showing map points with type-specific markers.
 *
 * - `selectedId` highlights a marker.
 * - `focus` ({ id, key }) flies to a point and opens its popup; change `key` to re-trigger.
 * - `flyTo` ({ lat, lng, zoom }) flies to arbitrary coordinates.
 * - `onMapClick` / `onMarkerDrag` turn the map into a position picker.
 */
const InteractiveMap = ({
  points = [],
  locationsById = {},
  selectedId = null,
  onSelect,
  focus = null,
  flyTo = null,
  fitToPoints = true,
  onMapClick,
  onMarkerDrag,
  showPopups = true,
  embed = false,
  className = "h-[420px]",
}) => {
  const markerRefs = useRef({});
  const validPoints = points.filter(
    (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)
  );

  return (
    // `relative z-0` isolates Leaflet's internal z-indexes from the site's fixed navbar.
    <div className={`relative z-0 w-full overflow-hidden rounded-lg ${className}`}>
      <MapContainer
        center={QUEBEC_CENTER}
        zoom={QUEBEC_ZOOM}
        scrollWheelZoom
        className="h-full w-full"
        style={{ background: "#0f172a" }}
      >
        <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
        <ResizeHandler />
        <FitToPoints points={validPoints} enabled={fitToPoints} />
        <FocusHandler focus={focus} points={validPoints} markerRefs={markerRefs} />
        <FlyToTarget target={flyTo} />
        {onMapClick && <ClickHandler onMapClick={onMapClick} />}

        {validPoints.map((point) => {
          const selected = point.id === selectedId;
          return (
            <Marker
              key={point.id}
              position={[point.lat, point.lng]}
              icon={createMarkerIcon(point.type, { selected })}
              draggable={!!onMarkerDrag}
              zIndexOffset={selected ? 1000 : point.type === "simple" ? 0 : 100}
              ref={(ref) => {
                if (ref) markerRefs.current[point.id] = ref;
                else delete markerRefs.current[point.id];
              }}
              eventHandlers={{
                click: () => onSelect?.(point.id),
                dragend: (e) => onMarkerDrag?.(point.id, e.target.getLatLng()),
              }}
            >
              {showPopups && (
                <Popup maxWidth={260} className="fqst-popup">
                  <PointPopup
                    point={point}
                    locationsById={locationsById}
                    embed={embed}
                  />
                </Popup>
              )}
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default InteractiveMap;
